import MassUpdateModal from '@/components/table/mass-update-modal';
import { useMissingDecimalsNotification } from '@/hooks/use-missing-decimals-notification';
import type { ColumnCategory } from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-types';
import { themeColors } from '@/theme/colors';
import { normalizeNumericString } from '@/utils/number';
import { Button } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import {
  Tooltip as MuiTooltip,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  type MRT_Cell,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_ExpandedState,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_SortingState,
  type MRT_TableInstance,
  type MRT_Updater,
  MaterialReactTable,
} from 'material-react-table';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import BulkActionsBar from './bulk-actions-bar';
import { EditCellInput } from './edit-cell-input';
import ExpandButton from './expand-button';
import MassRevertModal from './mass-revert-modal';
import { rmDisplayDecimals, round } from './table-format-helpers';
import {
  compareNumericValues,
  getColumnOrder,
  getCommentFilterOptions,
  getDecimals,
  getFilterOptions,
  getFontColor,
  getHeaderCategoryBackgroundColor,
  getHeaderCategorySubBackgroundColor,
  handleCellKeyDown,
  isCellChanged,
  isPriceEditUnchanged,
  isPriceFieldChanged,
  isRowChanged,
  matchesColumnFilter,
  runPricingEdit,
} from './table-helpers';
import type {
  BaseRowData,
  BaseRowDataForDisplay,
  ColumnAttributes,
  EditableField,
  LockableField,
  NumericFilterValue,
} from './table-types';
import FilterDropdown from './toolbar/filter-dropdown';
import NumericColumnFilter from './toolbar/numeric-column-filter';

const CHECKBOX_COLUMN_WIDTH = 50;

//TRow - the data used for calculations and storing in the backend
//TRowForDisplay - the data used for displaying in the table, with all fields as strings and additional fields for display purposes
//TColumn - the keys of TRow that are displayed as columns in the table

export type ColumnGroup<TColumn extends string> = {
  id: string;
  header: string;
  columnCategory: ColumnCategory;
  columnKeys: TColumn[];
};

type TableCoreProps<
  TRow extends BaseRowData,
  TRowForDisplay extends BaseRowDataForDisplay,
  TColumn extends string & keyof TRow,
> = {
  initialData: Map<string, TRow>;
  formattedInitialData: TRowForDisplay[];
  data: Map<string, TRow>;
  formattedData: TRowForDisplay[];
  columnData: Record<TColumn, ColumnAttributes>;
  columnGroups?: ColumnGroup<TColumn>[];
  planningYear: number;
  rowSelection: { items: TRow[]; state: MRT_RowSelectionState };
  lockedColumn: LockableField | undefined;
  tableHeight: string;
  pinnedColumns?: string[];
  groupedBy?: 'rm' | 'rrp';
  bulkActions?: React.ReactNode;
  allowEditingVat?: boolean;
  isReadOnly?: boolean;
  setRowSelection: Dispatch<
    SetStateAction<{
      state: MRT_RowSelectionState;
      items: TRow[];
    }>
  >;
  setData: (data: Map<string, TRow>) => void;
  calculatePricingField: (
    field: EditableField,
    row: TRow,
    value: number,
    lockedColumn: LockableField | undefined,
    originalLp: number,
  ) => TRow;
  isCellEditable: (columnKey: TColumn, row: TRowForDisplay) => boolean;
  cellComponent: (
    cell: MRT_Cell<TRowForDisplay>,
    key: TColumn,
  ) => React.ReactNode;
  getToolbar: (table: MRT_TableInstance<TRowForDisplay>) => React.ReactNode;
  onEdit?: (updatedRow: TRow, isRevertingChanges?: boolean) => void;
  onMassRevert?: (revertedRows: TRow[]) => void;
  customCellBackgroundColor?: (
    row: TRowForDisplay,
    columnKey: TColumn,
  ) => string | undefined;
  /** Highest-priority background override — takes precedence over isCellChanged yellow and customCellBackgroundColor. */
  overrideCellBackgroundColor?: (
    row: TRowForDisplay,
    columnKey: TColumn,
  ) => string | undefined;
  batchUpdatePricingField: (
    field: EditableField,
    value: number,
    isChangeProportional: boolean,
  ) => void;
  numericFilterColumns?: Set<string>;
  percentageColumns?: Set<string>;
  /** Session-proof baseline for revert and change detection.
   *  When provided, revert button and mass revert use this instead of initialData.
   *  Simulation passes a map built from immutable asIs* prices;
   *  Create Price passes comparisonBaseline (freshData from server). */
  revertBaseline?: Map<string, TRow>;
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: (updater: MRT_Updater<MRT_ColumnFiltersState>) => void;
};

const TableCore = <
  TRow extends BaseRowData,
  TRowForDisplay extends BaseRowDataForDisplay,
  TColumn extends string & keyof TRow,
>({
  initialData,
  formattedInitialData,
  data,
  formattedData,
  columnData,
  columnGroups,
  planningYear,
  rowSelection,
  lockedColumn,
  tableHeight,
  pinnedColumns,
  groupedBy,
  bulkActions,
  setRowSelection,
  setData,
  calculatePricingField,
  isCellEditable,
  cellComponent,
  getToolbar,
  onEdit,
  onMassRevert,
  customCellBackgroundColor,
  overrideCellBackgroundColor,
  allowEditingVat = false,
  isReadOnly = false,
  batchUpdatePricingField,
  numericFilterColumns = new Set<string>(),
  percentageColumns = new Set<string>(),
  revertBaseline,
  columnFilters,
  setColumnFilters,
}: TableCoreProps<TRow, TRowForDisplay, TColumn>) => {
  const theme = createTheme({
    palette: {
      mode: 'light',
    },
    components: {
      MuiBadge: {
        //hides a small badge that appears in the reports to show the sorting order
        styleOverrides: {
          badge: {
            display: 'none',
          },
        },
      },
    },
  });

  const isGrouped = Boolean(groupedBy);

  const formattedInitialDataMap = useMemo(
    () => new Map(formattedInitialData.map((r) => [r.primaryKey, r])),
    [formattedInitialData],
  );

  const [globalFilter, setGlobalFilter] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to run this when filters change
  useEffect(() => {
    if (!data) return;

    let hasExemptRows = false;
    for (const row of data.values()) {
      if (row.isExemptFromFiltering) {
        hasExemptRows = true;
        break;
      }
    }
    if (!hasExemptRows) return;

    const newMap = new Map<string, TRow>(data);
    for (const [key, row] of newMap) {
      if (row.isExemptFromFiltering) {
        newMap.set(key, { ...row, isExemptFromFiltering: false });
      }
    }
    setData(newMap);
  }, [columnFilters, globalFilter]);

  const showMissingDecimalsError = useMissingDecimalsNotification();

  const [pendingEditKey, setPendingEditKey] = useState<string | null>(null);
  const [cellToFocus, setCellToFocus] = useState<string | null>(null);

  // Update RRP/RM%/LP with recalculation based on locked column
  const updatePricingField = useCallback(
    (rowId: string, field: LockableField, value: number) => {
      const row = data.get(rowId);
      if (!row) return;
      const originalLp = initialData.get(rowId)?.lp ?? row.lp;
      const updated = runPricingEdit(
        () =>
          calculatePricingField(field, row, value, lockedColumn, originalLp),
        showMissingDecimalsError,
      );
      if (!updated) return;
      const newMap = new Map(data);
      newMap.set(rowId, { ...updated, isExemptFromFiltering: true });
      setData(newMap);
      onEdit?.(updated);
    },
    [
      data,
      lockedColumn,
      initialData,
      calculatePricingField,
      setData,
      onEdit,
      showMissingDecimalsError,
    ],
  );

  const handleCellEdit = useCallback(
    (
      cellValue: string,
      columnKey: string,
      rowIndex: MRT_Row<TRowForDisplay>,
    ) => {
      const rowId = rowIndex.original.primaryKey;
      const row = data.get(rowId);
      if (!row) return;

      const normalizedValue = normalizeNumericString(cellValue);

      if (columnKey === 'rrp' || columnKey === 'lp' || columnKey === 'rm') {
        const isPercentageField = columnKey === 'rm';
        const newValue = isPercentageField
          ? Number(normalizedValue) / 100
          : Number(normalizedValue);

        // Skip only when the committed value would not change. LP/RRP are rounded to the row's
        // currency decimals on commit, so focus→blur on a stored 56.004 shown as 56.00 is a no-op.
        // RM is shown with rmDisplayDecimals in the edit box and compared at that precision.
        let isUnchanged: boolean;
        if (isPercentageField) {
          isUnchanged =
            round(row.rm * 100, rmDisplayDecimals) ===
            round(newValue * 100, rmDisplayDecimals);
        } else {
          const decimals = runPricingEdit(
            () => getDecimals(row),
            showMissingDecimalsError,
          );
          if (decimals === null) return;
          isUnchanged = isPriceEditUnchanged(
            row[columnKey as 'rrp' | 'lp'],
            newValue,
            decimals,
          );
        }
        if (isUnchanged) {
          return;
        }

        updatePricingField(rowId, columnKey as LockableField, newValue);
      } else {
        // non-pricing fields (comments)
        const newMap = new Map(data);
        const updatedRow = {
          ...row,
          isExemptFromFiltering: true,
          [columnKey]: cellValue,
        };
        newMap.set(rowId, updatedRow);
        setData(newMap);
        onEdit?.(updatedRow);
      }
    },
    [showMissingDecimalsError, data, updatePricingField, setData, onEdit],
  );

  const columnKeys = Object.keys(columnData) as TColumn[];

  const [userColumnVisibility, setUserColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const columnVisibility = useMemo(
    () => ({
      ...Object.fromEntries(
        columnKeys.map((key) => [key, !columnData[key]?.hidden]),
      ),
      ...userColumnVisibility,
    }),
    [columnData, columnKeys, userColumnVisibility],
  );

  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: formattedData should not trigger re-expansion. Only grouping mode changes should reset expanded state.
  useEffect(() => {
    if (!isGrouped || formattedData.length === 0) return;

    const groupingColumn = groupedBy === 'rm' ? 'localLeadRm' : 'localLeadRrp';
    const uniqueFirstLevelValues = new Set(
      formattedData.map(
        (row) => row[groupingColumn as keyof TRowForDisplay] as string,
      ),
    );

    const expandedState: Record<string, boolean> = {};
    for (const value of uniqueFirstLevelValues) {
      expandedState[`${groupingColumn}:${value}`] = true;
    }

    if (groupedBy === 'rrp') {
      const uniquePairs = new Set(
        formattedData.map(
          (row) =>
            `${row[groupingColumn as keyof TRowForDisplay]}|${row['rrp' as keyof TRowForDisplay]}`,
        ),
      );
      for (const pair of uniquePairs) {
        const [llRrp, rrp] = pair.split('|');
        expandedState[`${groupingColumn}:${llRrp}>rrp:${rrp}`] = true;
      }
    }

    setExpanded(expandedState);
  }, [isGrouped, groupedBy]);

  const columnOrder = useMemo(
    () => getColumnOrder(groupedBy, columnKeys),
    [groupedBy, columnKeys],
  );

  const grouping = useMemo(
    () =>
      groupedBy === 'rm'
        ? ['localLeadRm', 'rm']
        : groupedBy === 'rrp'
          ? ['localLeadRrp', 'rrp', 'rrpIndexLocalLead']
          : [],
    [groupedBy],
  );

  const getDefaultSorting = useCallback(
    (gb: typeof groupedBy): MRT_SortingState =>
      gb === 'rm'
        ? [
            { id: 'localLeadRm', desc: false },
            { id: 'rm', desc: false },
          ]
        : gb === 'rrp'
          ? [
              { id: 'localLeadRrp', desc: false },
              { id: 'rrp', desc: false },
              { id: 'rrpIndexLocalLead', desc: false },
            ]
          : [],
    [],
  );

  const [sorting, setSorting] = useState<MRT_SortingState>(() =>
    getDefaultSorting(groupedBy),
  );

  useEffect(() => {
    setSorting(getDefaultSorting(groupedBy));
  }, [groupedBy, getDefaultSorting]);

  const isColumnExcluded = useCallback(
    (key: TColumn) => Boolean(columnData[key]?.excluded),
    [columnData],
  );

  const columns = useMemo<MRT_ColumnDef<TRowForDisplay>[]>(() => {
    const getHeaderGroup = (key: TColumn) =>
      columnGroups?.find((group) => group.columnKeys.includes(key));

    const dataColumns = (key: TColumn): MRT_ColumnDef<TRowForDisplay> => ({
      accessorKey: key,
      header: columnData[key]?.label ?? key,
      size: columnData[key]?.size,
      aggregationFn: columnData[key]?.aggregationFn,
      ...(numericFilterColumns.has(key) && {
        sortingFn: (rowA, rowB, columnId) =>
          compareNumericValues(
            rowA.getValue(columnId),
            rowB.getValue(columnId),
          ),
      }),
      GroupedCell: ({ cell, row }) => {
        return (
          <Flex alignItems="center" gap="2px">
            {row.depth === 0 ? (
              <>
                <Text className="body-xs-bold">{cell.getValue<string>()}</Text>
                <ExpandButton row={row} />
              </>
            ) : (
              <Text className="body-xs-bold">{cell.getValue<string>()}</Text>
            )}
          </Flex>
        );
      },
      AggregatedCell: ({ cell, row }) => {
        if (key === 'productCount' && row.getIsGrouped()) {
          const isDeepestGroup = !row.subRows?.[0]?.getIsGrouped();
          if (!isDeepestGroup) return null;
          return (
            <Flex align={'center'} gap="4px">
              <Text
                minWidth="30px"
                textAlign="right"
                className="label-xs-bold"
                color={themeColors.content.subtle}
              >
                {cell.getValue<number>()}
              </Text>
              <ExpandButton row={row} />
            </Flex>
          );
        }
        return null;
      },
      PlaceholderCell: ({ cell, row }) => {
        if (row.depth === 0) return null;
        if (row.getIsGrouped()) {
          return (
            <Text className="body-xs-bold">{cell.getValue<string>()}</Text>
          );
        }
        return cellComponent(cell, key as TColumn);
      },
      enablePinning: false,
      muiTableBodyCellProps: {
        align: columnData[key]?.alignment ?? 'left',
      },
      muiTableHeadCellProps: {
        align: columnData[key]?.alignment ?? 'left',
        sx: {
          backgroundColor: getHeaderCategorySubBackgroundColor(
            getHeaderGroup(key)?.columnCategory,
          ),

          //the only way to color pinned headers :/
          '&[data-pinned="true"]::before': {
            backgroundColor: getHeaderCategorySubBackgroundColor(
              getHeaderGroup(key)?.columnCategory,
            ),
          },
        },
      },
      enableEditing: (row) =>
        isCellEditable?.(key as TColumn, row.original) ?? false,
      enableColumnFilter: key !== 'primaryKey',
      filterFn: (row, columnId, filterValue) => {
        //keep exempt rows visible
        if (row.original.isExemptFromFiltering) return true;

        return matchesColumnFilter(
          String(row.original[columnId as keyof TRowForDisplay] ?? ''),
          columnId,
          filterValue,
          numericFilterColumns,
        );
      },
      Header: () => (
        <MuiTooltip title={columnData[key]?.label} placement="top">
          <Flex alignItems="center" gap="4px" minHeight={'28px'}>
            <Text
              className="body-xs-medium"
              color={getFontColor(columnData[key]?.columnType)}
            >
              {columnData[key]?.label}
            </Text>
          </Flex>
        </MuiTooltip>
      ),
      Cell: ({ cell }) => {
        if (key === 'productCount' && isGrouped) return null;
        return cellComponent(cell, key as TColumn);
      },
      Filter: ({ header }) => {
        if (numericFilterColumns.has(key as string)) {
          return (
            <NumericColumnFilter
              valueOptions={getFilterOptions(
                key as string,
                header.column
                  .getFacetedRowModel()
                  .flatRows.map((row) => row.original),
              )}
              value={
                header.column.getFilterValue() as NumericFilterValue | undefined
              }
              onChange={(value) => header.column.setFilterValue(value)}
              isPercentage={percentageColumns.has(key as string)}
            />
          );
        }

        if (key === 'comment') {
          return (
            <FilterDropdown
              options={getCommentFilterOptions()}
              onChange={(selected) =>
                header.column.setFilterValue(
                  selected.length > 0 ? selected : undefined,
                )
              }
              value={header.column.getFilterValue() as string[] | undefined}
              singleSelect
            />
          );
        }

        return (
          <FilterDropdown
            options={getFilterOptions(
              key as string,
              header.column
                .getFacetedRowModel()
                .flatRows.map((row) => row.original),
            )}
            onChange={(selected) =>
              header.column.setFilterValue(
                selected.length > 0 ? selected : undefined,
              )
            }
            value={header.column.getFilterValue() as string[]}
          />
        );
      },
      Edit: ({ cell, column, row, table }) => (
        <EditCellInput
          cell={cell}
          column={column}
          row={row}
          table={table}
          columnKey={key}
          data={data}
          pendingEditKey={pendingEditKey}
          planningYear={planningYear}
          setPendingEditKey={setPendingEditKey}
          setCellToFocus={setCellToFocus}
          handleCellEdit={handleCellEdit}
        />
      ),
    });

    if (columnGroups) {
      // When read-only the checkbox column is not rendered, so don't include its width in the offset
      let pinnedOffset = isReadOnly ? 0 : CHECKBOX_COLUMN_WIDTH;

      return columnGroups.map((group) => {
        const isGroupPinned = !groupedBy && pinnedColumns?.includes(group.id);

        //manual calculation of the left offset for each group header
        const groupLeft = pinnedOffset;
        if (isGroupPinned) {
          pinnedOffset += group.columnKeys
            .filter((key) => !isColumnExcluded(key))
            .reduce((sum, key) => sum + columnData[key]?.size, 0);
        }

        return {
          id: group.id,
          header: group.header,
          enableResizing: false,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: getHeaderCategoryBackgroundColor(
                group.columnCategory,
              ),
              fontWeight: 'bold',
              height: '28px',
              //unfortunately there's no simpler way to align the group text to the left
              '& .Mui-TableHeadCell-Content': {
                justifyContent: 'left',
                paddingLeft: '8px',
              },
              ...(isGroupPinned && {
                position: 'sticky',
                left: `${groupLeft}px`,
                zIndex: 1,
              }),
              // hide text for product-data group
              ...(group.id === 'product-data' && {
                color: 'transparent',
              }),
            },
          },
          columns: group.columnKeys
            .filter((key) => !isColumnExcluded(key))
            .map(dataColumns),
        };
      });
    }

    return columnKeys.filter((key) => !isColumnExcluded(key)).map(dataColumns);
  }, [
    columnKeys,
    columnData,
    columnGroups,
    isColumnExcluded,
    data,
    pendingEditKey,
    planningYear,
    handleCellEdit,
    cellComponent,
    isCellEditable,
    pinnedColumns,
    isGrouped,
    isReadOnly,
    groupedBy,
    numericFilterColumns,
    percentageColumns,
  ]);

  const [showMassUpdateModal, setShowMassUpdateModal] = useState(false);
  const [showMassRevertModal, setShowMassRevertModal] = useState(false);
  const massUpdateTriggerRef = useRef<HTMLButtonElement | null>(null);
  const massRevertTriggerRef = useRef<HTMLButtonElement | null>(null);
  const columnPinning = useMemo(
    () => (pinnedColumns && !groupedBy ? { left: pinnedColumns } : {}),
    [pinnedColumns, groupedBy],
  );

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <ThemeProvider theme={theme}>
        <MaterialReactTable
          columns={columns}
          data={formattedData}
          enableRowSelection={!isReadOnly}
          enableFacetedValues
          getRowId={(row) => row.primaryKey.toString()}
          state={{
            rowSelection: rowSelection.state,
            columnFilters,
            globalFilter,
            columnVisibility,
            columnOrder,
            expanded,
            columnPinning,
            grouping,
            sorting,
          }}
          onRowSelectionChange={(getNewState) => {
            const selectedRowIds =
              typeof getNewState === 'function'
                ? getNewState(rowSelection.state)
                : getNewState;

            const selectedItems = Array.from(data.values()).filter(
              (item) => selectedRowIds[item.primaryKey],
            );
            setRowSelection({ state: selectedRowIds, items: selectedItems });
          }}
          enableEditing={!isReadOnly}
          editDisplayMode="cell"
          initialState={{
            showColumnFilters: true,
            showGlobalFilter: true,
            density: 'compact',
          }}
          enablePagination={false}
          enableRowVirtualization
          muiTableBodyRowProps={({ row, table }) => ({
            sx: {
              backgroundColor: themeColors.surface.background.base.muted,
              height:
                table.getState().density === 'compact'
                  ? '30px'
                  : table.getState().density === 'comfortable'
                    ? '50px'
                    : '70px',
              // Hide intermediate rrp group rows in RRP report (depth 1)
              ...(groupedBy === 'rrp' &&
                row.depth === 1 &&
                row.getIsGrouped() && { display: 'none' }),
            },
          })}
          layoutMode="grid"
          onSortingChange={setSorting}
          enableColumnResizing
          displayColumnDefOptions={{
            'mrt-row-select': {
              //resize checkbox column
              size: CHECKBOX_COLUMN_WIDTH,
              enablePinning: false,
            },
            'mrt-row-expand': {
              //hide the expand group column in reports, we added our own expand buttons
              size: 0,
              muiTableHeadCellProps: { sx: { display: 'none' } },
              muiTableBodyCellProps: { sx: { display: 'none' } },
            },
          }}
          onColumnFiltersChange={setColumnFilters}
          onGlobalFilterChange={setGlobalFilter}
          onColumnVisibilityChange={setUserColumnVisibility}
          onExpandedChange={setExpanded}
          renderTopToolbar={({ table }) => getToolbar(table)}
          muiTableBodyCellProps={({ cell, column, table }) => {
            const isRowInExpandedSecondLevel =
              isGrouped && !cell.row.getIsGrouped() && groupedBy !== column.id;

            const isClickableForSelection =
              column.id !== 'mrt-row-select' &&
              !cell.row.getIsGrouped() &&
              !isCellEditable(column.id as TColumn, cell.row.original);

            return {
              ref: (element: HTMLTableCellElement | null) => {
                if (element && cellToFocus === cell.id) {
                  element.focus();
                  setCellToFocus(null);
                }
              },
              sx: (() => {
                const override = overrideCellBackgroundColor?.(
                  cell.row.original,
                  column.id as TColumn,
                );
                const isCellChangedFlag = isCellChanged(
                  cell.row.original,
                  column.id as keyof BaseRowDataForDisplay,
                  formattedInitialDataMap,
                );
                const isPinned = column.getIsPinned();

                let bgColor: string | undefined;
                if (override !== undefined) {
                  bgColor = override;
                } else if (isCellChangedFlag) {
                  bgColor = themeColors.support.warning.inverse;
                } else if (isPinned) {
                  bgColor = 'white';
                } else if (
                  isRowInExpandedSecondLevel ||
                  groupedBy === undefined
                ) {
                  bgColor = customCellBackgroundColor?.(
                    cell.row.original,
                    column.id as TColumn,
                  );
                }

                const isLastPinned = isPinned && column.getIsLastColumn('left');

                // Both the ::before background override and the divider box-shadow
                // need to go into the same '&[data-pinned="true"]' key — spreading
                // two separate objects with the same key would cause the second to
                // silently overwrite the first.
                const needsBeforeOverride =
                  isPinned && (override !== undefined || isCellChangedFlag);

                return {
                  backgroundColor: bgColor,
                  ...(needsBeforeOverride || isLastPinned
                    ? {
                        '&[data-pinned="true"]': {
                          // MRT pins cells using a ::before pseudo-element whose
                          // background is set at BOTH cell level and row level.
                          // The row-level rule wins on specificity so !important
                          // is the only reliable override.
                          ...(needsBeforeOverride && {
                            '&:before': {
                              backgroundColor: `${bgColor} !important`,
                            },
                          }),
                          // Re-apply the divider shadow with !important so it is
                          // never obscured by a coloured ::before background.
                          ...(isLastPinned && {
                            boxShadow:
                              '4px 0 8px -2px rgba(0,0,0,0.15) !important',
                          }),
                        },
                      }
                    : {}),
                  cursor: isClickableForSelection ? 'pointer' : undefined,
                };
              })(),
              tabIndex: 0,
              onClick: () => {
                if (isClickableForSelection) {
                  cell.row.toggleSelected();
                } else {
                  table.setEditingCell(cell);
                }
              },
              onKeyDown: (event) =>
                handleCellKeyDown({
                  event,
                  cell,
                  column,
                  table,
                  isCellEditable,
                  handleCellEdit,
                  setPendingEditKey,
                }),
            };
          }}
          enableColumnActions={false}
          enableKeyboardShortcuts
          enableStickyHeader
          renderBottomToolbar={({ table }) => (
            <Flex my={1} mx={2}>
              <Text fontSize={'11px'}>
                {table.getFilteredRowModel().rows.length.toLocaleString()} rows
              </Text>
            </Flex>
          )}
          enableGrouping={isGrouped}
          groupedColumnMode={isGrouped ? false : 'reorder'}
          enableColumnDragging={false}
          muiTableContainerProps={({ table }) => ({
            sx: {
              height: table.getState().isFullScreen
                ? 'calc(100vh - 100px)'
                : tableHeight,
              overflow: 'auto',
              position: 'relative',
              zIndex: 0,
            },
          })}
          muiSearchTextFieldProps={{
            placeholder: '',
            variant: 'outlined',
            style: {
              width: '200px',
              borderRadius: '8px',
            },
            size: 'small',
          }}
          enableColumnPinning
          globalFilterFn="contains"
        />
      </ThemeProvider>
      {Boolean(rowSelection.items.length) && (
        <BulkActionsBar
          selectedItemsCount={rowSelection.items.length}
          onDeselectAll={() => setRowSelection({ state: {}, items: [] })}
          position="absolute"
          actions={
            bulkActions ?? (
              <Flex gap="16px" align={'center'}>
                <Button
                  aria-label="Edit"
                  iconName="pen-solid"
                  iconPosition="start"
                  size={'small'}
                  style={{ backgroundColor: 'transparent', padding: 0 }}
                  ref={massUpdateTriggerRef}
                  onClick={() => setShowMassUpdateModal(true)}
                >
                  Edit
                </Button>
                <Button
                  aria-label="Revert edits"
                  iconName="undo"
                  iconPosition="start"
                  size={'small'}
                  style={{
                    backgroundColor: 'transparent',
                    padding: '0',
                  }}
                  ref={massRevertTriggerRef}
                  onClick={() => setShowMassRevertModal(true)}
                  disabled={
                    !rowSelection.items.some((row) => {
                      const baseline = revertBaseline ?? initialData;
                      const original = baseline.get(row.primaryKey);
                      const current = data.get(row.primaryKey);
                      return revertBaseline
                        ? isPriceFieldChanged(original, current)
                        : isRowChanged(original, current);
                    })
                  }
                >
                  Revert edits
                </Button>
              </Flex>
            )
          }
        />
      )}
      <MassUpdateModal
        isOpen={showMassUpdateModal}
        triggerRef={massUpdateTriggerRef}
        lockedColumn={lockedColumn}
        selectedRows={rowSelection.items}
        allowEditingVat={allowEditingVat}
        onClose={() => setShowMassUpdateModal(false)}
        onSubmit={(
          field: EditableField | LockableField,
          value: number,
          isChangeProportional: boolean,
        ) => {
          batchUpdatePricingField(field, value, isChangeProportional);
          setShowMassUpdateModal(false);
          setRowSelection({ state: {}, items: [] });
        }}
      />
      <MassRevertModal
        isOpen={showMassRevertModal}
        triggerRef={massRevertTriggerRef}
        selectedRowsCount={rowSelection.items.length}
        hasCommentColumn={columnKeys.includes('comment' as TColumn)}
        onClose={() => setShowMassRevertModal(false)}
        onSubmit={() => {
          const baseline = revertBaseline ?? initialData;
          const newMap = new Map(data);
          const revertedRows: TRow[] = [];
          for (const row of rowSelection.items) {
            const originalRow = baseline.get(row.primaryKey);
            if (!originalRow) continue;
            const revertedRow =
              'comment' in row
                ? { ...originalRow, comment: row.comment }
                : originalRow;
            newMap.set(row.primaryKey, revertedRow);
            revertedRows.push(revertedRow);
          }
          if (onMassRevert) {
            onMassRevert(revertedRows);
          } else {
            for (const revertedRow of revertedRows) {
              onEdit?.(revertedRow, true);
            }
          }
          setData(newMap);
          setShowMassRevertModal(false);
          setRowSelection({ state: {}, items: [] });
        }}
      />
    </div>
  );
};
export default TableCore;
